import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertColor,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
    ChangeEvent,
    FocusEvent,
    FormEvent,
    SyntheticEvent,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    AddOutlined,
    CancelOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    InfoOutlined,
    SaveOutlined,
    WarningOutlined,
} from '@mui/icons-material';
import { Dealer } from '../../types/Dealer';
import { InfoMessage, InfoMessageProps } from '../../components/InfoMessage';
import ApiService from '../../ApiService';
import { TradeInMarket } from '../../types/TradeInMarket';
import FormSection from '../../components/FormSection';
import { TabsContainer } from '../../components/Tabs';
import TabPanel from '../../components/TabPanel';
import { AmcInstance } from '../../types/AmcInstance';
import DealerImportSites from './DealerImportSites';
import SasAccountComponent from './SasAccountComponent';
import DealerExportSites from './DealerExportSites';
import { UserContext } from '../../App';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import AccessGuard, { hasAccess } from '../../components/AccessGuard';
import { USER_GROUP_ADMIN, USER_GROUP_AGENCY } from '../../types/User';
import DealerCreativeAssets from './DealerCreativeAssets';
import DealerDetailsEdit, { DealerUpdateCallback } from './DealerDetailsEdit';
import ActiveStatus from '../../components/ActiveStatus';
import { Dv360Advertiser, DspAdvertiser, DspEntity } from '../../types/DspEntity';
import { CDTextField } from '../../components/CDTextField';
import PolkSalesTab from './PolkSalesTab';
import AdTagBuilder from './AdTagBuilder';
import DealerGatewayAPI from './DealerGatewayAPI';

import AppBreadcrumbs from '../../components/AppBreadcrumbs';
import Block from '../../components/Block';
import Column from '../../components/Column';
import HAT from './HAT';
import { Agency } from '../../types/Agency';
import usePageTitle from '../../hooks/usePageTitle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlatformLogo from '../../components/Logo/PlatformLogo';

const TABS = {
    settings: { label: 'Settings', slug: 'settings' },
    'creative-assets': { label: 'Creative Assets', slug: 'creative-assets' },
    'dsp-integration-map': { label: 'Dsp Integration Map', slug: 'dsp-integration-map' },
    'trade-in-dco': { label: 'Trade In DCO', slug: 'trade-in-dco' },
    'import-sites': { label: 'Import Sites', slug: 'import-sites' },
    'export-sites': { label: 'Export Sites', slug: 'export-sites' },
    creative: { label: 'Creative', slug: 'creative' },
    hat: { label: 'HAT', slug: 'hat' },
    'reported-sales': { label: 'Reported Sales', slug: 'reported-sales' },
    'gateway-api': { label: 'Gateway API', slug: 'gateway-api' },
} as const;

type TabSlug = keyof typeof TABS;

function isTabSlug(value: string): value is TabSlug {
    return value in TABS;
}

const DEFAULT_TAB_ID = TABS.settings.slug;

const DealerEditPage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { hash } = useLocation();
    const id = params.id ? +params.id : null;
    const [agency, setAgency] = useState<Agency | undefined>(new Agency());
    const [dealer, setDealer] = useState<Dealer>(new Dealer());
    const [markets, setMarkets] = useState<TradeInMarket[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [amcInstances, setAmcInstances] = useState<AmcInstance[]>([]);
    const [infoMessage, setInfoMessage] = useState<InfoMessageProps>({
        message: null,
    });

    const [duplicateDv360Advertiser, setDuplicateDv360Advertiser] = useState(false);
    const [dv360Advertiser, setDv360Advertiser] = useState<Dv360Advertiser | null>(null);
    const [dv360Entities, setDv360Entities] = useState<DspEntity[]>([]);
    const [dspEntities, setDspEntities] = useState<DspEntity[]>([]);
    const initialTabId = params.tab && isTabSlug(params.tab) ? params.tab : DEFAULT_TAB_ID;
    const [selectedTab, setSelectedTab] = useState<TabSlug>(initialTabId);
    const [duplicateDspAdvertiser, setDuplicateDspAdvertiser] = useState(false);
    const [showNewAdvertiserMessage, setShowNewAdvertiserMessage] = useState(false);
    const [hasDefaultDspEntity, setHasDefaultDspEntity] = useState(false);
    const [hasDefaultSasAccount, setHasDefaultSasAccount] = useState(false);
    const [duplicate, setDuplicate] = useState(false);
    const [duplicateId, setDuplicateId] = useState<number | undefined>();
    const [dspAdvertiser, setDspAdvertiser] = useState<DspAdvertiser | null>(null);
    const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
    const [loadingDspAdvertiser, setLoadingDspAdvertiser] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    const submitForm = (): void => {
        if (formRef.current) {
            formRef.current.requestSubmit();
        }
    };

    const { userContext } = useContext(UserContext);

    const showPageActions: boolean | undefined = useMemo(() => {
        return selectedTab !== TABS['reported-sales'].slug;
    }, [selectedTab]);

    const advertiserName = dealer.dealerName && params.id !== 'new' ? dealer.dealerName : null;
    const segment = !params.id ? 'Add New Advertiser' : TABS[selectedTab].label;
    usePageTitle({ entity: advertiserName, segments: [segment] });

    useEffect(() => {
        if (!params.id) return; // Skip tab logic for new advertisers
        if (!params.tab) return; // No tab param, let AppRoutes handle redirect

        if (!isTabSlug(params.tab)) {
            navigate(`/advertisers/${params.id}/${DEFAULT_TAB_ID}`, { replace: true });
        } else {
            setSelectedTab(params.tab);
        }
    }, [params.tab, navigate, params.id]);

    useEffect(() => {
        if (id) {
            ApiService.getDealerOverview(id)
                .then((response) => {
                    const dealerOverview = response.data;
                    const dealer = dealerOverview.dealer;
                    setAmcInstances(dealerOverview.amcInstances);
                    setMarkets(dealerOverview.markets);
                    setDealer(dealer);
                    setHasDefaultDspEntity(dealer.dspEntityId !== null);
                    setHasDefaultSasAccount(dealer.sasAccountId !== null);
                    setLoaded(true);
                    lookupDspEntitiesByAgency(dealer.agency?.id);

                    if (userContext.isAdmin() || userContext.isOpsAdmin()) {
                        if (dealer.dv360Advertiser) {
                            getDv360Advertiser(dealer.dv360Advertiser);
                        }
                    }

                    ApiService.isDuplicate(dealer)
                        .then((response) => {
                            let duplicates = response.data;
                            let hasDuplicate = duplicates.length > 0;
                            setDuplicate(hasDuplicate);
                            if (hasDuplicate) {
                                showInfoMessage('error', 'Duplicate Advertiser name.');
                            }
                        })
                        .catch(() => {});
                })
                .catch((error) => {
                    if (error?.response?.status === 404) {
                        navigate('/notFound');
                    }
                });
        } else {
            ApiService.getDefaultDealer()
                .then((response) => {
                    setDealer({
                        ...response.data,
                        agencyId: userContext.agencyId,
                    });
                    setHasDefaultDspEntity(response.data.dspEntityId !== null);
                    setHasDefaultSasAccount(response.data.sasAccountId !== null);
                    setLoaded(true);
                })
                .catch(() => {});
        }
    }, [navigate, id, userContext, userContext.agencyId]);

    useEffect(() => {
        if (dealer.agencyId) {
            lookupDspEntitiesByAgency(dealer.agencyId);
        } else {
            setDspEntities([]);
            setDv360Entities([]);
        }
    }, [dealer.agencyId]);

    useEffect(() => {
        if (!dealer.dspAdvertiser || dspEntities.length === 0 || !(userContext.isAdmin() || userContext.isOpsAdmin())) {
            if (!dealer.dspAdvertiser) {
                setDspAdvertiser(null);
                setDuplicateDspAdvertiser(false);
                setLoadingDspAdvertiser(false);
            }
            return;
        }
        const amazonEntityIds = dspEntities.map((entity) => entity.id);
        const timer = setTimeout(() => {
            ApiService.getDealerByDspAdvertiserId(dealer.dspAdvertiser!)
                .then(({ data: dealers }) => {
                    const duplicate = dealers.find((d) => d.id !== dealer.id);
                    if (duplicate) {
                        showInfoMessage('error', 'Duplicate ADSP Advertiser : ' + duplicate.dealerName);
                        setDuplicateDspAdvertiser(true);
                        setDspAdvertiser(null);
                        setLoadingDspAdvertiser(false);
                    } else {
                        setDuplicateDspAdvertiser(false);
                        lookupDspAdvertiser(amazonEntityIds, dealer.dspAdvertiser!);
                    }
                })
                .catch(() => {
                    setDuplicateDspAdvertiser(false);
                    lookupDspAdvertiser(amazonEntityIds, dealer.dspAdvertiser!);
                });
        }, 500);
        return () => clearTimeout(timer);
    }, [dspEntities, dealer.dspAdvertiser, dealer.id, userContext]);

    useEffect(() => {
        if (!dealer.dv360Advertiser) {
            setDv360Advertiser(null);
            setDuplicateDv360Advertiser(false);
            return;
        }
        if (!(userContext.isAdmin() || userContext.isOpsAdmin())) return;
        const timer = setTimeout(() => {
            ApiService.getDealerByDv360AdvertiserId(dealer.dv360Advertiser!)
                .then(({ data: dealers }) => {
                    const duplicate = dealers.find((d) => d.id !== dealer.id);
                    if (duplicate) {
                        showInfoMessage('error', 'Duplicate DV360 Advertiser : ' + duplicate.dealerName);
                        setDuplicateDv360Advertiser(true);
                        setDv360Advertiser(null);
                    } else {
                        setDuplicateDv360Advertiser(false);
                        getDv360Advertiser(dealer.dv360Advertiser!);
                    }
                })
                .catch(() => {
                    setDuplicateDv360Advertiser(false);
                    getDv360Advertiser(dealer.dv360Advertiser!);
                });
        }, 500);
        return () => clearTimeout(timer);
    }, [dealer.dv360Advertiser, dealer.id, userContext]);

    useEffect(() => {
        if (hash) {
            switch (hash) {
                case '#polkSales':
                    setSelectedTab(TABS['reported-sales'].slug);
                    break;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hash]);

    function showInfoMessage(alertColor: AlertColor, message: string) {
        setInfoMessage({
            message: message,
            severity: alertColor,
            onClose: () => {
                setInfoMessage({ message: null });
            },
        });
    }

    function lookupDspEntitiesByAgency(agencyId: number | undefined) {
        if (!agencyId) {
            setDspEntities([]);
            setDv360Entities([]);
            return;
        }

        ApiService.getDspEntitiesByAgencyId(agencyId)
            .then((response) => {
                const amazonEntities = response.data.filter((entity: DspEntity) => entity.platform.slug === 'amazon');
                const dv360Only = response.data.filter((entity: DspEntity) => entity.platform.slug === 'dv360');

                setDspEntities(amazonEntities);
                setDv360Entities(dv360Only);
            })
            .catch(() => {
                setDspEntities([]);
                setDv360Entities([]);
            });
    }

    function modifyDealer(update: Partial<Dealer>) {
        setDealer((prev) => ({ ...prev, ...update, modified: true }));
    }

    function handleToggleStatus() {
        const updatedActiveStatus = !dealer.activeStatus;
        modifyDealer({ activeStatus: updatedActiveStatus });
    }

    function handleAgencyDetails(agency?: Agency) {
        setAgency(agency);
    }

    const handleDealerDetailsUpdate: DealerUpdateCallback = (name, update) => {
        modifyDealer(update);
    };

    function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
        const { target } = event;
        const field = target.name;

        if (
            field === 'dspTechnologyReportType' ||
            field === 'tradeInEnrolled' ||
            field.startsWith('dspReport') ||
            field.startsWith('tfTokenIsActive') ||
            field === 'gatewayEnabled' ||
            field === 'creativeStudioIsActive'
        ) {
            modifyDealer({ [field]: target.checked ?? false });
        } else if (field === 'tradeInMarketId') {
            modifyDealer({ tradeInMarket: { id: +target.value } });
        } else {
            let value: string = target.value;

            switch (field) {
                case 'dspAdvertiser':
                case 'dv360Advertiser':
                    value = value.replace(/\s/g, ''); // Remove spaces
                    break;
            }
            modifyDealer({ [field]: value });
        }
    }

    function getDv360Advertiser(advertiserId: string) {
        if (!advertiserId) {
            setDv360Advertiser(null);
            return;
        }

        ApiService.getDv360Advertiser(advertiserId)
            .then(({ data }) => {
                setDv360Advertiser(data ?? null);
            })
            .catch(() => {
                setDv360Advertiser(null);
            });
    }

    function handleFormBlur(event: FocusEvent<HTMLFormElement>) {
        const target = event.target;
        const isTextInput = target instanceof HTMLInputElement && target.type === 'text';
        const isTextArea = target instanceof HTMLTextAreaElement;
        if (isTextInput || isTextArea) {
            const { name, value } = target;

            if (name) {
                modifyDealer({ [name]: value.trim() });
            }
        }
    }

    function lookupDspAdvertiser(dspEntityIds: number[], dspAdvertiserId: string) {
        if (!dspEntityIds || dspEntityIds.length === 0 || !dspAdvertiserId) {
            setDspAdvertiser(null);
            setLoadingDspAdvertiser(false);
            return;
        }

        setLoadingDspAdvertiser(true);

        const tryNextEntity = (index: number): void => {
            if (index >= dspEntityIds.length) {
                setDspAdvertiser(null);
                setLoadingDspAdvertiser(false);
                return;
            }

            ApiService.getDspAdvertiser(dspEntityIds[index], dspAdvertiserId)
                .then((response) => {
                    if (response.data) {
                        setDspAdvertiser(response.data);
                        setLoadingDspAdvertiser(false);
                    } else {
                        tryNextEntity(index + 1);
                    }
                })
                .catch(() => {
                    tryNextEntity(index + 1);
                });
        };

        tryNextEntity(0);
    }

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!dealer.agencyId) {
            return;
        }

        // if (dealer.dv360Advertiser && !dv360Advertiser?.advertiserId) {
        //     showInfoMessage(
        //         'error',
        //         'DV360 Advertiser ID is incorrect or it is not related to the chosen DV360 Entity!'
        //     );
        //     return;
        // }

        if (dealer.amcInstanceId && +dealer.amcInstanceId < 1) {
            dealer.amcInstanceId = undefined;
        }
        let isNew = !dealer.id;
        if (isNew && agency) {
            const { name, tfCustomerId, tfUserIsActive, creativeStudioIsActive, creativeStudioUserId } = agency;
            dealer.agency = {
                id: dealer.agencyId,
                name,
                tfCustomerId: tfCustomerId ?? '',
                tfUserIsActive,
                creativeStudioIsActive,
                creativeStudioUserId,
            };
        }
        setDuplicate(false);

        const dealerToSave = { ...dealer };

        // Trim trailing spaces from all string fields before saving
        for (const key of Object.keys(dealerToSave)) {
            if (typeof (dealerToSave as any)[key] === 'string') {
                (dealerToSave as any)[key] = ((dealerToSave as any)[key] as string).trim();
            }
        }

        if (dealerToSave.dv360Advertiser && dv360Entities.length > 0) {
            dealerToSave.dv360EntityId = dv360Entities[0].id;
        }

        if (dealerToSave.dspAdvertiser && dspEntities.length > 0) {
            dealerToSave.dspEntityId = dspEntities[0].id;
        }

        ApiService.updateDealer(dealerToSave)
            .then((response) => {
                if (isNew && userContext.isAgency()) {
                    setShowNewAdvertiserMessage(true);
                } else {
                    showInfoMessage('success', 'Advertiser has been updated.');
                }

                if (response.data.id) {
                    return ApiService.getDealerOverview(response.data.id);
                }
                return { data: { dealer: response.data } };
            })
            .then((overviewResponse) => {
                const updatedDealer = overviewResponse.data.dealer || overviewResponse.data;
                setDealer(updatedDealer);

                if (updatedDealer.agencyId) {
                    lookupDspEntitiesByAgency(updatedDealer.agencyId);
                }
            })
            .catch((error) => {
                if (error.response?.status === 400) {
                    let duplicates = error.response.data;
                    setDuplicate(true);
                    if (!userContext.isAgency()) {
                        showInfoMessage('error', 'Duplicate Advertiser name.');
                    }
                    if (duplicates.length > 0) {
                        setDuplicateId(duplicates[0].id);
                    }
                } else {
                    showInfoMessage('error', error.response?.data || error.message);
                }
            });
    }

    function selectTab(_event: SyntheticEvent, value: TabSlug) {
        setSelectedTab(value);
        if (params.id != null && params.id !== 'new') {
            navigate(`/advertisers/${params.id}/${value}`);
        }
    }

    const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    const getDv360ValidationIcon = () => {
        if (!dv360Advertiser) {
            return <CancelOutlined color="error" />;
        }

        const advertiserNameMatch = dv360Advertiser.displayName === dealer.dealerName;

        if (advertiserNameMatch) {
            return <CheckCircleOutlined color="success" />;
        } else {
            return (
                <Tooltip title="Advertiser found in DV360 but names do not match">
                    <WarningOutlined color="warning" />
                </Tooltip>
            );
        }
    };

    const getDv360ValidationMessage = () => {
        if (!dv360Advertiser) {
            return 'Advertiser not found in DV360';
        }
        return dv360Advertiser.displayName || dv360Advertiser.advertiserId.toString();
    };

    if (!loaded) {
        return <></>;
    }

    return (
        <Column gap={0} flex={1}>
            <Block>
                <Column gap={1}>
                    <AppBreadcrumbs
                        items={[{ label: 'Advertisers', href: '/advertisers' }, { label: dealer.dealerName || '...' }]}
                    />
                </Column>
            </Block>

            <TabsContainer>
                <Tabs value={selectedTab} onChange={selectTab}>
                    {hasAccess(userContext, `${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`) && (
                        <Tab value={TABS.settings.slug} label={TABS.settings.label} style={{ textTransform: 'none' }} />
                    )}
                    {(hasAccess(userContext, USER_GROUP_ADMIN) || userContext.isOpsAdmin()) && (
                        <Tab value={TABS['creative-assets'].slug} label={TABS['creative-assets'].label} />
                    )}
                    {(hasAccess(userContext, USER_GROUP_ADMIN) || userContext.isOpsAdmin()) &&
                        (!dealer.sasAccountId || !dealer.dspEntityId) && (
                            <Tab
                                value={TABS['dsp-integration-map'].slug}
                                label={<Alert severity="error">{TABS['dsp-integration-map'].label}</Alert>}
                                style={{ textTransform: 'none' }}
                            />
                        )}
                    {(hasAccess(userContext, USER_GROUP_ADMIN) || userContext.isOpsAdmin()) &&
                        dealer.sasAccountId &&
                        dealer.dspEntityId && (
                            <Tab
                                value={TABS['dsp-integration-map'].slug}
                                label={
                                    !dealer.agencyId ? (
                                        <Alert severity="error">{TABS['dsp-integration-map'].label}</Alert>
                                    ) : (
                                        TABS['dsp-integration-map'].label
                                    )
                                }
                                style={{ textTransform: 'none' }}
                            />
                        )}
                    {(hasAccess(userContext, USER_GROUP_ADMIN) || userContext.isOpsAdmin()) && (
                        <Tab value={TABS['import-sites'].slug} label={TABS['import-sites'].label} />
                    )}
                    {(hasAccess(userContext, USER_GROUP_ADMIN) || userContext.isOpsAdmin()) && (
                        <Tab value={TABS['export-sites'].slug} label={TABS['export-sites'].label} />
                    )}
                    {(hasAccess(userContext, USER_GROUP_ADMIN) || userContext.isOpsAdmin()) && (
                        <Tab
                            value={TABS['trade-in-dco'].slug}
                            label={TABS['trade-in-dco'].label}
                            style={{ textTransform: 'none' }}
                        />
                    )}

                    {hasAccess(userContext, `${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`) && (
                        <Tab value={TABS.hat.slug} label={TABS.hat.label} />
                    )}
                    {hasAccess(userContext, `${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`) && (
                        <Tab value={TABS['reported-sales'].slug} label="Reported Sales" />
                    )}
                </Tabs>
            </TabsContainer>
            <form ref={formRef} onSubmit={handleSubmit} onBlur={handleFormBlur}>
                <Column>
                    <Block disableGutters={selectedTab === TABS.hat.slug}>
                        <AccessGuard accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}>
                            <TabPanel index={TABS.settings.slug} value={selectedTab}>
                                <FormControlLabel
                                    sx={{ marginBottom: '8px' }}
                                    control={
                                        <Tooltip title="Set the Active Status for this Advertiser.  Inactivating hides this Advertiser from selection when creating Campaigns and other areas. Setting a dealer to inactive will pause all import and export activity.">
                                            <Switch
                                                checked={dealer.activeStatus}
                                                onChange={handleToggleStatus}
                                                name={'activeStatus'}
                                            />
                                        </Tooltip>
                                    }
                                    label={<ActiveStatus activeStatus={dealer.activeStatus} />}
                                />

                                {!dealer.activeStatus && (
                                    <Alert
                                        severity="warning"
                                        sx={{
                                            mb: 2,
                                            mt: 0.5,
                                            maxWidth: '800px',
                                            alignItems: 'center',
                                        }}
                                    >
                                        Import and export sites will remain disabled if this advertiser is reactivated
                                        and must be turned back on manually.
                                    </Alert>
                                )}

                                <Box sx={{ maxWidth: '800px' }}>
                                    <DealerDetailsEdit
                                        dealer={dealer}
                                        hasDefaultSasAccount={hasDefaultSasAccount}
                                        hasDefaultDspEntity={hasDefaultDspEntity}
                                        duplicateId={duplicate ? duplicateId : undefined}
                                        onDealerUpdate={handleDealerDetailsUpdate}
                                        shrinkTextFieldLabels={true}
                                        onAgencyDetails={handleAgencyDetails}
                                    />
                                </Box>
                            </TabPanel>
                        </AccessGuard>

                        <AccessGuard
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                            condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                        >
                            <div>
                                <TabPanel index={TABS['trade-in-dco'].slug} value={selectedTab}>
                                    <Grid container spacing={2}>
                                        <Grid size={3}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={dealer.tradeInEnrolled}
                                                        name="tradeInEnrolled"
                                                        onChange={handleInputChange}
                                                    />
                                                }
                                                label="Enrolled"
                                            />
                                        </Grid>
                                        <Grid size={5}>
                                            <TextField
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                label="Select a Market"
                                                name="tradeInMarketId"
                                                select
                                                value={dealer.tradeInMarket?.id + ''}
                                                disabled={!dealer.tradeInEnrolled}
                                                onChange={handleInputChange}
                                                slotProps={{
                                                    select: { native: false },
                                                }}
                                            >
                                                {markets.map((market) => {
                                                    return (
                                                        <MenuItem key={market.id + ''} value={market.id + ''}>
                                                            {market.marketName}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </TextField>
                                        </Grid>
                                        <Grid size={4} />

                                        <Grid size={4}>
                                            <Button
                                                component="button"
                                                onClick={() => navigate(`/tradeInOffers/${dealer.id}`)}
                                                disabled={
                                                    dealer.modified || !dealer.tradeInEnrolled || !dealer.tradeInMarket
                                                }
                                            >
                                                Offers
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel index={TABS['dsp-integration-map'].slug} value={selectedTab}>
                                    <Box sx={{ width: '100%' }}>
                                        <Accordion
                                            expanded={expandedPanel === 'amazon'}
                                            onChange={handleAccordionChange('amazon')}
                                            sx={{
                                                mb: 2,
                                                '&:before': {
                                                    display: 'none',
                                                },
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    backgroundColor: '#fff',
                                                    borderRadius: '8px',
                                                    '&:hover': {
                                                        backgroundColor: '#f5f5f5',
                                                    },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <PlatformLogo size="Small" platform="AMAZON" />
                                                    <Typography variant="h6" fontWeight={500}>
                                                        Amazon DSP
                                                    </Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails
                                                sx={{
                                                    backgroundColor: '#fafafa',
                                                    borderTop: '1px solid #e0e0e0',
                                                    p: 3,
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: '#fff',
                                                    },
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                    Configure Amazon DSP settings for this Advertiser.
                                                </Typography>

                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid size={4}>
                                                        <SasAccountComponent
                                                            sasAccountId={dealer.sasAccountId}
                                                            onSelectSasAccount={(accountId: number) => {
                                                                if (accountId !== dealer.sasAccountId) {
                                                                    modifyDealer({
                                                                        sasAccountId: accountId,
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid size={8} />

                                                    <Grid size={4}>
                                                        <TextField
                                                            name="dspEntity"
                                                            label="Dsp Entity"
                                                            size="small"
                                                            fullWidth={true}
                                                            disabled={true}
                                                            variant="outlined"
                                                            value={
                                                                dspEntities
                                                                    .map((entity) => entity.entityName)
                                                                    .join(', ') || ''
                                                            }
                                                            slotProps={{
                                                                inputLabel: { shrink: true },
                                                                input: {
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <Box
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 0.5,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="text.secondary"
                                                                                    sx={{ fontSize: '0.75rem' }}
                                                                                >
                                                                                    (Read Only)
                                                                                </Typography>
                                                                                <InfoOutlined
                                                                                    sx={{
                                                                                        fontSize: '1rem',
                                                                                        color: 'text.secondary',
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        </InputAdornment>
                                                                    ),
                                                                },
                                                            }}
                                                            sx={{
                                                                '& .MuiInputBase-input.Mui-disabled': {
                                                                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                                                                },
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid size={8} />

                                                    <AccessGuard
                                                        accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                                        condition={() =>
                                                            userContext.isAdmin() || userContext.isOpsAdmin()
                                                        }
                                                    >
                                                        <>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    name="dspAdvertiser"
                                                                    label="ADSP Advertiser ID"
                                                                    size="small"
                                                                    fullWidth={true}
                                                                    error={duplicateDspAdvertiser}
                                                                    variant="outlined"
                                                                    disabled={dspEntities.length === 0}
                                                                    onChange={handleInputChange}
                                                                    value={dealer.dspAdvertiser}
                                                                    slotProps={{
                                                                        inputLabel: { shrink: true },
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid size={8}>
                                                                <CDTextField
                                                                    value={
                                                                        loadingDspAdvertiser
                                                                            ? 'Validating...'
                                                                            : dspAdvertiser
                                                                            ? dspAdvertiser.name
                                                                            : 'Invalid Advertiser ID'
                                                                    }
                                                                    disabled={true}
                                                                    fullWidth
                                                                    slotProps={{
                                                                        input: {
                                                                            startAdornment: (
                                                                                <InputAdornment position="start">
                                                                                    {loadingDspAdvertiser ? (
                                                                                        <CircularProgress size={20} />
                                                                                    ) : dspAdvertiser &&
                                                                                      dspAdvertiser?.name &&
                                                                                      dspAdvertiser?.name ===
                                                                                          dealer.dealerName ? (
                                                                                        <CheckCircleOutlined color="success" />
                                                                                    ) : (
                                                                                        <span
                                                                                            title={
                                                                                                dspAdvertiser &&
                                                                                                dspAdvertiser?.name !==
                                                                                                    dealer.dealerName
                                                                                                    ? 'Advertiser names do not match'
                                                                                                    : ''
                                                                                            }
                                                                                        >
                                                                                            <CancelOutlined color="error" />
                                                                                        </span>
                                                                                    )}
                                                                                </InputAdornment>
                                                                            ),
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            {dealer.agency?.creativeStudioIsActive &&
                                                                dealer.agency?.creativeStudioUserId && (
                                                                    <>
                                                                        <Grid size={8} />

                                                                        <Grid size={4}>
                                                                            <Grid>
                                                                                <FormSection>
                                                                                    <Typography variant="h6">
                                                                                        Creative Studio Advertiser
                                                                                    </Typography>
                                                                                    <FormControlLabel
                                                                                        control={
                                                                                            <Switch
                                                                                                checked={
                                                                                                    dealer.creativeStudioIsActive
                                                                                                }
                                                                                                name="creativeStudioIsActive"
                                                                                                onChange={
                                                                                                    handleInputChange
                                                                                                }
                                                                                            />
                                                                                        }
                                                                                        label={
                                                                                            <ActiveStatus
                                                                                                activeStatus={
                                                                                                    dealer.creativeStudioIsActive
                                                                                                }
                                                                                            />
                                                                                        }
                                                                                    />
                                                                                    {dealer.creativeStudioIsActive && (
                                                                                        <>
                                                                                            Creative Studio Advertiser
                                                                                            ID:{' '}
                                                                                            {
                                                                                                dealer.creativeStudioUserId
                                                                                            }
                                                                                        </>
                                                                                    )}
                                                                                </FormSection>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </>
                                                                )}

                                                            <Grid size={4}>
                                                                <TextField
                                                                    variant="outlined"
                                                                    fullWidth
                                                                    size="small"
                                                                    label="AMC Instance"
                                                                    name="amcInstanceId"
                                                                    select
                                                                    value={
                                                                        dealer.amcInstanceId
                                                                            ? dealer.amcInstanceId
                                                                            : '-1'
                                                                    }
                                                                    disabled={false}
                                                                    onChange={handleInputChange}
                                                                    slotProps={{
                                                                        select: { native: false },
                                                                    }}
                                                                >
                                                                    <MenuItem key={'-1'} value={'-1'}>
                                                                        None
                                                                    </MenuItem>

                                                                    {amcInstances.map((amc) => {
                                                                        return (
                                                                            <MenuItem
                                                                                key={amc.id + ''}
                                                                                value={amc.id + ''}
                                                                            >
                                                                                {amc.name}
                                                                            </MenuItem>
                                                                        );
                                                                    })}
                                                                </TextField>
                                                            </Grid>

                                                            <Grid size={12}>
                                                                <AdTagBuilder
                                                                    dealer={dealer}
                                                                    showInfoMessage={showInfoMessage}
                                                                    handleInputChange={handleInputChange}
                                                                />
                                                            </Grid>

                                                            <Grid sx={{ mt: 2 }} size={8}>
                                                                <Grid>
                                                                    <FormSection>
                                                                        <Typography variant="h6">
                                                                            DSP API Reports
                                                                        </Typography>

                                                                        <Grid container spacing={3}>
                                                                            <Grid>
                                                                                <FormControlLabel
                                                                                    control={
                                                                                        <Switch
                                                                                            checked={
                                                                                                dealer.dspReportImpressions
                                                                                            }
                                                                                            name="dspReportImpressions"
                                                                                            onChange={handleInputChange}
                                                                                            disabled={
                                                                                                !dspAdvertiser ||
                                                                                                !dspAdvertiser.advertiserId
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                    label="Impressions"
                                                                                />
                                                                            </Grid>

                                                                            <Grid>
                                                                                <FormControlLabel
                                                                                    control={
                                                                                        <Switch
                                                                                            checked={
                                                                                                dealer.dspReportSummaryGeo
                                                                                            }
                                                                                            name="dspReportSummaryGeo"
                                                                                            onChange={handleInputChange}
                                                                                            disabled={
                                                                                                !dspAdvertiser ||
                                                                                                !dspAdvertiser.advertiserId
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                    label="Summary GEO"
                                                                                />
                                                                            </Grid>

                                                                            <Grid>
                                                                                <FormControlLabel
                                                                                    control={
                                                                                        <Switch
                                                                                            checked={
                                                                                                dealer.dspReportSummaryAppBySpend
                                                                                            }
                                                                                            name="dspReportSummaryAppBySpend"
                                                                                            onChange={handleInputChange}
                                                                                            disabled={
                                                                                                !dspAdvertiser ||
                                                                                                !dspAdvertiser.advertiserId
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                    label="Summary App by Spend"
                                                                                />
                                                                            </Grid>
                                                                            <Grid>
                                                                                <FormControlLabel
                                                                                    control={
                                                                                        <Switch
                                                                                            checked={
                                                                                                dealer.dspTechnologyReportType
                                                                                            }
                                                                                            name="dspTechnologyReportType"
                                                                                            onChange={handleInputChange}
                                                                                            disabled={
                                                                                                !dspAdvertiser ||
                                                                                                !dspAdvertiser.advertiserId
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                    label="Technology Summary"
                                                                                />
                                                                            </Grid>
                                                                        </Grid>
                                                                    </FormSection>
                                                                </Grid>
                                                            </Grid>

                                                            <Grid size={8}>
                                                                <Grid>
                                                                    <FormSection>
                                                                        <DealerGatewayAPI
                                                                            dealer={dealer}
                                                                            onDealerUpdate={handleDealerDetailsUpdate}
                                                                        />
                                                                    </FormSection>
                                                                </Grid>
                                                            </Grid>

                                                            <Grid size={12} />
                                                        </>
                                                    </AccessGuard>
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion
                                            expanded={expandedPanel === 'dv360'}
                                            onChange={handleAccordionChange('dv360')}
                                            sx={{
                                                mb: 2,
                                                '&:before': {
                                                    display: 'none',
                                                },
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    backgroundColor: '#fff',
                                                    borderRadius: '8px',
                                                    '&:hover': {
                                                        backgroundColor: '#f5f5f5',
                                                    },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <PlatformLogo size="Small" platform="DV360" />
                                                    <Typography variant="h6" fontWeight={500}>
                                                        Google DV360
                                                    </Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails
                                                sx={{
                                                    backgroundColor: '#fafafa',
                                                    borderTop: '1px solid #e0e0e0',
                                                    p: 3,
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: '#fff',
                                                    },
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                    Configure Google DV360 settings for this Advertiser.
                                                </Typography>

                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid size={4}>
                                                        <TextField
                                                            name="dv360Entity"
                                                            label="DV360 Entity"
                                                            size="small"
                                                            fullWidth={true}
                                                            disabled={true}
                                                            variant="outlined"
                                                            value={
                                                                dv360Entities
                                                                    .map((entity) => entity.entityName)
                                                                    .join(', ') || ''
                                                            }
                                                            slotProps={{
                                                                inputLabel: { shrink: true },
                                                                input: {
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <Box
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 0.5,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="text.secondary"
                                                                                    sx={{ fontSize: '0.75rem' }}
                                                                                >
                                                                                    (Read Only)
                                                                                </Typography>
                                                                                <InfoOutlined
                                                                                    sx={{
                                                                                        fontSize: '1rem',
                                                                                        color: 'text.secondary',
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        </InputAdornment>
                                                                    ),
                                                                },
                                                            }}
                                                            sx={{
                                                                '& .MuiInputBase-input.Mui-disabled': {
                                                                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                                                                },
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid size={8} />

                                                    <Grid size={4}>
                                                        <TextField
                                                            name="dv360Advertiser"
                                                            label="DV360 Advertiser ID"
                                                            size="small"
                                                            fullWidth={true}
                                                            error={duplicateDv360Advertiser}
                                                            variant="outlined"
                                                            disabled={dv360Entities.length === 0}
                                                            onChange={handleInputChange}
                                                            value={dealer.dv360Advertiser}
                                                            slotProps={{
                                                                inputLabel: { shrink: true },
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid size={8}>
                                                        <CDTextField
                                                            value={getDv360ValidationMessage()}
                                                            disabled={true}
                                                            fullWidth
                                                            slotProps={{
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            {getDv360ValidationIcon()}
                                                                        </InputAdornment>
                                                                    ),
                                                                },
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Box>
                                </TabPanel>

                                <TabPanel index={TABS['import-sites'].slug} value={selectedTab}>
                                    <DealerImportSites dealer={dealer} />
                                </TabPanel>

                                <TabPanel index={TABS['export-sites'].slug} value={selectedTab}>
                                    <DealerExportSites dealer={dealer} onChange={(dealer) => modifyDealer(dealer)} />
                                </TabPanel>

                                <AccessGuard
                                    accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                    condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                                >
                                    <>
                                        <TabPanel index={TABS['creative-assets'].slug} value={selectedTab}>
                                            <Box sx={{ maxWidth: '800px' }}>
                                                <DealerCreativeAssets
                                                    dealer={dealer}
                                                    onDealerUpdate={handleDealerDetailsUpdate}
                                                    shrinkTextFieldLabels={true}
                                                />
                                            </Box>
                                        </TabPanel>
                                    </>
                                </AccessGuard>
                            </div>
                        </AccessGuard>

                        <AccessGuard accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}>
                            <TabPanel index={TABS.hat.slug} value={selectedTab} sx={{ p: 0 }}>
                                <HAT
                                    dealer={dealer}
                                    handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                                        handleInputChange(event);

                                        setTimeout(() => {
                                            submitForm();
                                        }, 500);
                                    }}
                                />
                            </TabPanel>
                        </AccessGuard>

                        <AccessGuard accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}>
                            <>
                                <TabPanel index={TABS['reported-sales'].slug} value={selectedTab}>
                                    <PolkSalesTab dealer={dealer} />
                                </TabPanel>
                            </>
                        </AccessGuard>
                    </Block>

                    {showPageActions === true && (
                        <>
                            <Divider />

                            <Block py={0}>
                                {showNewAdvertiserMessage && (
                                    <>
                                        {' '}
                                        <Alert style={{ marginBottom: '15px' }} severity="success">
                                            Your new advertiser is ready. Next, Launch a New Campaign to get ads
                                            running!{' '}
                                        </Alert>
                                        <Link
                                            style={{ paddingTop: '200px' }}
                                            to="/campaign/v2/create"
                                            state={{ dealerId: dealer.id }}
                                        >
                                            <Button variant="contained" color="primary" startIcon={<AddOutlined />}>
                                                New Campaign
                                            </Button>
                                        </Link>
                                    </>
                                )}
                                {!showNewAdvertiserMessage && (
                                    <Grid container spacing={2}>
                                        <Grid size={12}>
                                            <Stack direction="row" spacing={2}>
                                                <Button
                                                    disabled={
                                                        duplicateDspAdvertiser ||
                                                        duplicateDv360Advertiser ||
                                                        !dealer.modified ||
                                                        !dealer.sasAccountId ||
                                                        !dealer.agencyId
                                                    }
                                                    color="primary"
                                                    type="submit"
                                                    variant="contained"
                                                    startIcon={<SaveOutlined />}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => navigate('/advertisers')}
                                                    color="error"
                                                    startIcon={<CloseOutlined />}
                                                >
                                                    Cancel
                                                </Button>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                )}
                            </Block>
                        </>
                    )}
                </Column>
            </form>
            <InfoMessage {...infoMessage} />
        </Column>
    );
};

export default DealerEditPage;
